// frontend/src/UserProfile.tsx - User Profile and Password Management

import React, { useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';
import { useAuth } from './AuthContext';

interface UserProfileProps {
  onBack: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  
  // Profile edit form state
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || ''
  });
  
  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = Cookies.get('access_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  // Validate password strength
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one digit');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  // Handle profile update
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await axios.put(`${API_URL}/api/auth/users/${user?.id}`, {
        full_name: profileForm.full_name,
        email: profileForm.email
      }, {
        headers: getAuthHeaders()
      });

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setEditingProfile(false);
      // Note: In a full app, you'd want to refresh the user context here
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setPasswordErrors([]);

    // Validate new password
    const validationErrors = validatePassword(passwordForm.new_password);
    if (validationErrors.length > 0) {
      setPasswordErrors(validationErrors);
      setLoading(false);
      return;
    }

    // Check password confirmation
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: getAuthHeaders()
      });

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to change password';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      padding: '24px', 
      maxWidth: '800px', 
      margin: '0 auto'
    }}>
      
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: '600',
          color: '#1A1A1A',
          marginBottom: '8px'
        }}>
          Account Settings
        </h1>
          <p style={{ color: '#6B7280' }}>
            Manage your account information and security settings
          </p>
        </div>

      {/* User Information Card */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ 
            fontSize: '20px',
            fontWeight: '600',
            margin: 0,
            color: '#1A1A1A'
          }}>
            Profile Information
          </h2>
          {!editingProfile && (
            <button
              onClick={() => {
                setEditingProfile(true);
                setProfileForm({
                  full_name: user?.full_name || '',
                  email: user?.email || ''
                });
              }}
              className="btn btn-secondary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              Edit Profile
            </button>
          )}
        </div>
        
        {editingProfile ? (
          <form onSubmit={handleProfileUpdate}>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '500',
                  marginBottom: '4px',
                  color: '#6B7280'
                }}>
                  Full Name
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '500',
                  marginBottom: '4px',
                  color: '#6B7280'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  className="form-input"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '500',
                  marginBottom: '4px',
                  color: '#6B7280'
                }}>
                  Username
                </label>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  color: '#6B7280',
                  fontSize: '14px'
                }}>
                  {user?.username} (cannot be changed)
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '500',
                  marginBottom: '4px',
                  color: '#6B7280'
                }}>
                  Role
                </label>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  color: '#6B7280',
                  fontSize: '14px'
                }}>
                  {user?.role?.replace(/_/g, ' ')} (cannot be changed)
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingProfile(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontWeight: '500',
                marginBottom: '4px',
                color: '#6B7280'
              }}>
                Full Name
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                color: '#1A1A1A'
              }}>
                {user?.full_name}
              </div>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontWeight: '500',
                marginBottom: '4px',
                color: '#6B7280'
              }}>
                Username
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                color: '#1A1A1A'
              }}>
                {user?.username}
              </div>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontWeight: '500',
                marginBottom: '4px',
                color: '#6B7280'
              }}>
                Email
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                color: '#1A1A1A'
              }}>
                {user?.email}
              </div>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontWeight: '500',
                marginBottom: '4px',
                color: '#6B7280'
              }}>
                Role
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                color: '#1A1A1A'
              }}>
                {user?.role?.replace(/_/g, ' ')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Change Password Card */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ 
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '16px',
          color: '#1A1A1A'
        }}>
          Change Password
        </h2>
        
        <form onSubmit={handlePasswordChange}>
          <div style={{ display: 'grid', gap: '16px' }}>
            
            <div>
              <label htmlFor="current_password" className="form-label">
                Current Password
              </label>
              <input
                type="password"
                id="current_password"
                className="form-input"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                required
              />
            </div>
            
            <div>
              <label htmlFor="new_password" className="form-label">
                New Password
              </label>
              <input
                type="password"
                id="new_password"
                className="form-input"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                required
              />
              {passwordErrors.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {passwordErrors.map((error, index) => (
                    <div key={index} style={{ 
                      color: '#ef4444', 
                      fontSize: '14px',
                      marginTop: '4px'
                    }}>
                      • {error}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <label htmlFor="confirm_password" className="form-label">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirm_password"
                className="form-input"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                required
              />
            </div>
            
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
              
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onBack}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Message Display */}
      {message && (
        <div style={{
          padding: '12px',
          borderRadius: '8px',
          marginTop: '16px',
          backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
          color: message.type === 'success' ? '#065f46' : '#dc2626'
        }}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default UserProfile;